import constants from '@/constants/constants.ts';

const APP_ID = constants.plainAppId;
const SCRIPT_ID = 'plain-chat-sdk';
const SCRIPT_SRC = 'https://chat.cdn-plain.com/index.js';
const SUPPORT_EMAIL_URL = 'mailto:support@kay.ai';
// Chats from every Kay surface land in one Plain inbox, and Plain records no
// originating URL, so the product is only distinguishable by what we tag here.
const PRODUCT_LABEL_TYPE_ID = import.meta.env.VITE_PLAIN_PRODUCT_LABEL_ID;
const SUPPORT_OPEN_FALLBACK_MS = 8000;
const CHAT_STYLE = {
  launcherBackgroundColor: '#FFE100',
  launcherIconColor: '#111111',
} as const;

export interface SupportUser {
  email?: string;
  emailHash?: string;
  fullName?: string;
  id: string;
  shortName?: string;
}

interface PlainCustomerDetails {
  email?: string;
  emailHash?: string;
  externalId: string;
  fullName?: string;
  shortName?: string;
}

interface PlainThreadDetails {
  labelTypeIds?: string[];
}

interface PlainSDK {
  close: () => void;
  init: (options: {
    appId: string;
    customerDetails: PlainCustomerDetails;
    hideBranding: boolean;
    style: typeof CHAT_STYLE;
    threadDetails?: PlainThreadDetails;
  }) => Promise<void>;
  isInitialized: () => boolean;
  open: () => void;
  setCustomerDetails: (details: PlainCustomerDetails | null) => Promise<void>;
}

declare global {
  interface Window {
    Plain?: PlainSDK;
  }
}

let currentUser: SupportUser | null = null;
let identityVersion = 0;
let initialization: Promise<void> | null = null;
let pendingOpen = false;
let plainLoadFailed = false;
let supportFallbackTimer: number | null = null;

function clearPendingOpen() {
  pendingOpen = false;
  if (supportFallbackTimer !== null) {
    window.clearTimeout(supportFallbackTimer);
    supportFallbackTimer = null;
  }
}

function openSupportFallback() {
  if (!pendingOpen) {
    return;
  }
  clearPendingOpen();
  window.location.href = SUPPORT_EMAIL_URL;
}

function toCustomerDetails(user: SupportUser): PlainCustomerDetails {
  return {
    externalId: user.id,
    ...(user.fullName && { fullName: user.fullName }),
    ...(user.shortName && { shortName: user.shortName }),
    ...(user.email &&
      user.emailHash && {
        email: user.email,
        emailHash: user.emailHash,
      }),
  };
}

async function syncCurrentUser() {
  const plain = window.Plain;
  const user = currentUser;
  if (!plain || !user) {
    return;
  }

  const versionAtStart = identityVersion;

  try {
    if (plain.isInitialized()) {
      await plain.setCustomerDetails(toCustomerDetails(user));
    } else {
      initialization ??= plain.init({
        appId: APP_ID,
        customerDetails: toCustomerDetails(user),
        hideBranding: true,
        style: CHAT_STYLE,
        ...(PRODUCT_LABEL_TYPE_ID && {
          threadDetails: { labelTypeIds: [PRODUCT_LABEL_TYPE_ID] },
        }),
      });
      await initialization;
    }

    // Authentication can change while Plain.init() is still in flight. Apply
    // the latest state after initialization so an old user cannot survive a
    // sign-out or fast account switch on the same browser.
    if (versionAtStart !== identityVersion) {
      await plain.setCustomerDetails(
        currentUser ? toCustomerDetails(currentUser) : null,
      );
      if (!currentUser) {
        plain.close();
      }
    }

    if (pendingOpen && plain.isInitialized()) {
      clearPendingOpen();
      plain.open();
    }
  } catch (error) {
    console.error('[plain] failed to initialize or identify user:', error);
    openSupportFallback();
  } finally {
    initialization = null;
  }
}

export function loadPlain(user: SupportUser) {
  currentUser = user;
  identityVersion += 1;

  if (window.Plain) {
    void syncCurrentUser();
    return;
  }
  if (document.getElementById(SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = false;
  script.onload = () => void syncCurrentUser();
  script.onerror = () => {
    plainLoadFailed = true;
    console.error('[plain] SDK failed to load');
    openSupportFallback();
  };
  document.head.append(script);
}

export function openSupportChat() {
  pendingOpen = true;

  if (window.Plain?.isInitialized()) {
    clearPendingOpen();
    window.Plain.open();
    return;
  }

  if (plainLoadFailed) {
    openSupportFallback();
    return;
  }

  supportFallbackTimer ??= window.setTimeout(
    openSupportFallback,
    SUPPORT_OPEN_FALLBACK_MS,
  );

  if (window.Plain && currentUser) {
    void syncCurrentUser();
  }
}

export function resetPlain() {
  currentUser = null;
  identityVersion += 1;
  clearPendingOpen();

  const plain = window.Plain;
  if (!plain) {
    return;
  }

  plain.close();
  // Passing null is Plain's logout path. It invalidates the prior customer
  // session instead of merely replacing visible profile fields.
  void plain.setCustomerDetails(null).catch((error) => {
    console.error('[plain] failed to clear customer session:', error);
  });
}
